import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../components/Global/Loader';
import { FaEye } from 'react-icons/fa';
import { MdDeleteForever } from "react-icons/md";

function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setStudents(response.data.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (student) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/student/${studentToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setShowDeleteModal(false);
      setStudentToDelete(null);
      fetchStudents(); // Refresh the list
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);

    }
  };

  const handleViewDetails = (student) => {
    navigate('/admin/student-record', {
      state: { studentData: student }
    });
  };

  const filteredStudents = students.filter((student) => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      student.studentId?.toLowerCase().includes(searchTerm) ||
      student.firstName?.toLowerCase().includes(searchTerm) ||
      student.lastName?.toLowerCase().includes(searchTerm) ||
      student.email?.toLowerCase().includes(searchTerm) ||
      student.cellPhone?.toLowerCase().includes(searchTerm) ||
      student.cnic?.toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div id="main-content" className="relative pl-36 mt-16 w-full p-8 min-h-screen">
      {loading && <Loader />}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[30%]">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the student record for{' '}
              <span className="font-semibold">
                {studentToDelete?.firstName} {studentToDelete?.lastName}
              </span>
              ? This action cannot be undone!
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setStudentToDelete(null);
                }}
                className="px-4 py-2 text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Students</h1>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search students..."
              className="py-2 px-3 w-64 text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 transition duration-300 shadow-sm"              style={{ borderBottomWidth: "3px", borderBottomColor: "#3b82f6" }} // Tailwind blue-500 color
            />
            <i className="fas fa-search absolute right-3 top-3 text-gray-400"></i>
          </div>
        </div>
        <table className="w-full table-auto border-t border-b border-gray-300">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="px-4 py-2 text-center"></th>
              <th className="px-4 py-2 text-center text-gray-400">ID</th>
              <th className="px-4 py-2 text-center text-gray-400">Name</th>
              <th className="px-4 py-2 text-center text-gray-400">Email</th>
              <th className="px-4 py-2 text-center text-gray-400">Phone</th>
              <th className="px-4 py-2 text-center text-gray-400">Cnic No.</th>
              <th className="px-4 py-2 text-center text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredStudents) && filteredStudents.length > 0 ? (
              filteredStudents.map((student) => (
                <tr key={student._id} className="border-b border-gray-300 text-center hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <img
                      src={student?.profileURL ? student.profileURL : "/Assets/default_user.png"} alt="User Profile"
                      className="w-12 h-12 rounded-full object-cover border border-gray-200 mx-auto" />
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{student.studentId}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{student.firstName + " " + student.lastName}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{student.email}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{student.cellPhone}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{student.cnic}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    <div className="px-4 flex items-center justify-center space-x-3">
                      <FaEye
                        className="text-blue-500 cursor-pointer hover:text-blue-600 size-5"
                        onClick={() => handleViewDetails(student)}
                        
                      />
                      <MdDeleteForever
                        className="text-red-500 cursor-pointer hover:text-red-600"
                        onClick={() => handleDelete(student)}
                        size={22}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">

                    <p className="text-sm text-gray-400 mt-1">
                      No match found
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      {error && (
        <div className="text-center text-red-500 mt-4">
          {error}
        </div>
      )}
    </div>
  );
}

export default Students;